import { useEffect, useState, type JSX } from 'react';

import {
  Center,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  Spinner,
  Text,
  VStack,
} from '@vimeo/bokeh';
import { SearchMagnifier, InfoCircle } from '@vimeo/bokeh-icons';
import { User } from '@vimeo/gctl-api/models/components/schemas/user/User';
import { translate } from '@vimeo/i18n-runtime';
import { VideoCard } from '@vimeo/playlist';

import { useInfiniteScroll } from './use-infinite-scroll';

export type SearchResultType = {
  alt: string;
  duration: number;
  name: string;
  src: string;
  srcSet: string;
  user: User;
  onClick: VoidFunction;
};

export interface SearchResultsProps {
  resultsPending: boolean;
  resultsList: SearchResultType[] | null;
  searchedTerm: string;
  canLoadMore?: boolean;
  searchError?: boolean;
  onLoadMoreSearchResults?: () => void;
  onResultClick?: () => void;
  onDismissSearch?: () => void;
  onTabFromResultsList?: () => void;
  onTabShiftFromResultsList?: () => void;
  popoverContentRef?: React.RefObject<HTMLDivElement | null>;
  resultsListRef?: React.RefObject<HTMLDivElement | null>;
}

export const SearchResults = ({
  resultsPending,
  resultsList,
  searchedTerm,
  searchError,
  canLoadMore = false,
  onLoadMoreSearchResults,
  onResultClick,
  onDismissSearch,
  onTabFromResultsList,
  onTabShiftFromResultsList,
  popoverContentRef,
  resultsListRef,
}: SearchResultsProps) => {
  /**
   * This effect will fire when the current is behind the next page. This occurs when the user has scrolled
   * lastElementRef into view and canLoadMore is true.
   */
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPage, lastElementRef] = useInfiniteScroll(canLoadMore);
  useEffect(() => {
    if (nextPage > currentPage) {
      onLoadMoreSearchResults?.();
      setCurrentPage(nextPage);
    }
  }, [currentPage, setCurrentPage, onLoadMoreSearchResults, nextPage]);

  // Don't show popover if there is no active search
  if (!resultsPending && !searchedTerm) {
    return null;
  }

  // If results are pending, no results or there is an error, shift
  // focus from the popover to the 'clear search' button on blur
  const handleBlurContent = () => {
    if (resultsPending || searchError || !resultsList?.length) {
      onTabFromResultsList?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, onClick?: VoidFunction) => {
    const focusAndScroll = (elem: Element) => {
      (elem as HTMLElement)?.focus();
      elem?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    };

    e.preventDefault();
    switch (e.key) {
      case 'Enter':
      case ' ':
        onResultClick?.();
        onClick?.();
        break;
      case 'Home':
        if (e.currentTarget.parentElement) {
          const firstElem = e.currentTarget.parentElement.firstElementChild;
          if (firstElem) focusAndScroll(firstElem);
        }
        break;
      case 'End':
        if (e.currentTarget.parentElement) {
          const lastElem = e.currentTarget.parentElement.lastElementChild;
          if (lastElem) focusAndScroll(lastElem);
        }
        break;
      case 'ArrowUp':
        const prevSibling = e.currentTarget.previousElementSibling;
        if (prevSibling) focusAndScroll(prevSibling);
        break;
      case 'ArrowDown':
        const nextSibling = e.currentTarget.nextElementSibling;
        if (nextSibling) focusAndScroll(nextSibling);
        break;
      case 'Tab':
        if (e.shiftKey) {
          onTabShiftFromResultsList?.();
        } else {
          onTabFromResultsList?.();
        }
        break;
      case 'Escape':
        onDismissSearch?.();
        break;
    }
  };

  const replacements = {
    SEARCHED_TERM: searchedTerm,
    BOLD: (text: string) => (
      <Text as="span" variant="heading-xs" fontWeight="bold">
        {text}
      </Text>
    ),
    PLAIN: (text: string) => (
      <Text as="span" variant="body-md">
        {text}
      </Text>
    ),
  };

  const resultsFor = translate({
    singular: '{PLAIN}Results for{/PLAIN} {BOLD}“{SEARCHED_TERM}”{/BOLD}',
    replacements,
  });

  const hasResults = Array.isArray(resultsList) && resultsList.length > 0;

  let searchResultsContent: JSX.Element | null = null;
  switch (true) {
    case !!searchedTerm && hasResults:
      searchResultsContent = (
        <>
          {!resultsPending ? (
            <PopoverHeader p={75}>
              <Text variant="heading-xs" color="text-primary">
                {resultsFor}
              </Text>
            </PopoverHeader>
          ) : null}
          <PopoverBody overflowY="auto" pr={2}>
            <VStack spacing="2px" align="flex-start" mt={2} ref={resultsListRef}>
              {resultsList?.map(({ onClick, ...result }, i) => (
                <VideoCard
                  key={`${i}-${result.name}`}
                  ref={i === resultsList.length - 1 ? lastElementRef : undefined}
                  {...result}
                  onClick={() => {
                    onClick?.();
                    onResultClick?.();
                  }}
                  onKeyDown={(e) => handleKeyDown(e, onClick)}
                  borderRadius="md"
                  direction="row"
                  variant="static"
                  scrollMargin="2px"
                  px={75}
                  py={2}
                  my={2}
                  ml={1}
                  w="100%"
                />
              ))}
            </VStack>
          </PopoverBody>
        </>
      );
      break;

    case resultsPending:
      searchResultsContent = <ResultsPending />;
      break;

    case searchError:
      searchResultsContent = <NoResults isError />;
      break;

    default:
      searchResultsContent = <NoResults />;
  }

  return (
    <PopoverContent
      minH="lg"
      maxH={{ base: 'calc(100vh - 80px)', md: 'calc(100vh - 136px)' }}
      justifyContent="center"
      pl="2"
      pr={0}
      py="2"
      ref={popoverContentRef}
      onBlur={handleBlurContent}
    >
      {searchResultsContent}
    </PopoverContent>
  );
};

const NoResults = ({ isError }: { isError?: boolean }) => (
  <PopoverBody overflowY="visible" pr={2}>
    <VStack spacing="sm" align="center" py="md">
      {isError ? (
        <InfoCircle textColor="text-primary" boxSize="sm" />
      ) : (
        <SearchMagnifier textColor="text-primary" boxSize="sm" />
      )}
      <Text variant="heading-md" textColor="text-primary" px={75}>
        {isError ? translate("Couldn't load results") : translate('No matching results')}
      </Text>
      <Text variant="heading-xs" textColor="text-secondary" px={75}>
        {isError ? translate('Reload the page and try again') : translate('Try another search')}
      </Text>
    </VStack>
  </PopoverBody>
);

const ResultsPending = () => (
  <PopoverBody overflowY="visible" pr={2}>
    <Center h="192px">
      <Spinner />
    </Center>
  </PopoverBody>
);
